import React, { useEffect, useRef } from "react";

const TURNSTILE_SCRIPT_URI =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise;

// Loads the Turnstile script once and shares it between all widgets
function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_URI;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = undefined;
        script.remove();
        reject(new Error("Could not load Cloudflare Turnstile"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

// "sv-SE" -> "sv", "nb-NO" -> "nb" etc.
function toTurnstileLanguage(language) {
  return language ? language.split("-")[0] : "auto";
}

/**
 * Cloudflare Turnstile widget. Calls onTokenChange with a token when the
 * challenge is solved, and with null when the token expires or fails.
 * Tokens are single use, so the widget must be remounted after each submit.
 */
function TurnstileWidget({ action, language, onTokenChange }) {
  const containerRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  onTokenChangeRef.current = onTokenChange;

  useEffect(() => {
    let cancelled = false;
    let widgetId;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) {
          return;
        }
        widgetId = turnstile.render(containerRef.current, {
          sitekey: String(process.env.REACT_APP_TURNSTILE_SITE_KEY),
          action: action,
          language: toTurnstileLanguage(language),
          callback: (token) => onTokenChangeRef.current(token),
          "expired-callback": () => onTokenChangeRef.current(null),
          "error-callback": () => onTokenChangeRef.current(null),
        });
      })
      .catch((error) => console.log(error));

    return () => {
      cancelled = true;
      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [action, language]);

  return <div ref={containerRef} className="d-flex justify-content-center mt-3" />;
}

export default TurnstileWidget;
